import { FC, useEffect, useMemo, useState } from "react";

import { DateRangePicker } from "../atoms/DataRangePicker";
import { t } from "i18next";
import { User } from "@/app/auth/auth";
import { FormikConfig, FormikProps } from "formik";
import { DateTime } from "luxon";
import { Select } from "../organisms/Select";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { currencies } from "@/app/fees/fees";
import { MultiSelect } from "../organisms/MultiSelect";
import { MultiSelectWithAllOption } from "../organisms/MultiSelectWithAllOption";
import { Button, Dialog } from "@headlessui/react";
import { ButtonPrimary } from "../atoms/ButtonPrimary";

export type AccountingFilterProps = {
  setVisible: (visible: boolean) => void;
  visible?: boolean;
  personal: Array<any>;
  formik: FormikProps<any>;
  currencies: Array<any>;
  branches: Array<any>;
};

export const AccountingFilter: FC<AccountingFilterProps> = ({
  setVisible,
  visible,
  formik,
  personal = [],
  currencies = [],
  branches = [],
}) => {
  return (
    <Dialog
      open={visible}
      onClose={() => setVisible(false)}
      className="relative z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      {/* Modal wrapper */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white w-full max-w-4xl rounded-2xl shadow-xl p-6 space-y-6">
          <header className="flex flex-1 justify-end">
            <button
              type="button"
              className="px-4 cursor-default text-3xl text-gray-400 hover:text-gray-500"
              onClick={() => setVisible(false)}
            >
              <span className="sr-only">Close</span>
              &times;
            </button>
          </header>
          <div className="flex flex-col mt-4 lg:flex-row gap-4 justify-center items-center w-full mb-4">
            <div className="col-span-full w-full flex-1">
              <InputWithCornerHint
                label="Monto mínimo"
                name="minAmount"
                value={formik.values.minAmount}
                onChange={formik.handleChange}
              />
            </div>
            <div className="col-span-full w-full flex-1">
              <InputWithCornerHint
                label="Monto máximo"
                name="maxAmount"
                value={formik.values.maxAmount}
                onChange={formik.handleChange}
              />
            </div>
            <div className="col-span-full w-full flex-1">
              <Select
                name="currency"
                selected={
                  currencies.find(
                    (option) => option.value == formik.values.currency
                  ) || currencies[0]
                }
                setSelected={(option) =>
                  formik.setFieldValue("currency", option.value)
                }
                options={currencies}
                tittle="Divisa"
                textDescribe=""
              />
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-4 justify-center items-center w-full">
            <div className="col-span-full w-full flex-1">
              <DateRangePicker
                id="date"
                label={t("maintenance.views.inter_dates")}
                value={{
                  startDate: formik.values.startDate
                    ? DateTime.fromFormat(
                        formik.values.startDate,
                        "yyyy-MM-dd"
                      ).toJSDate()
                    : new Date(),
                  endDate: formik.values.endDate
                    ? DateTime.fromFormat(
                        formik.values.endDate,
                        "yyyy-MM-dd"
                      ).toJSDate()
                    : new Date(),
                }}
                onDateChange={(startDate, endDate) => {
                  formik.setFieldValue(
                    "startDate",
                    DateTime.fromJSDate(startDate).toFormat("yyyy-MM-dd")
                  );
                  formik.setFieldValue(
                    "endDate",
                    DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd")
                  );
                }}
              />
            </div>
            <div className="w-full lg:col-span-full lg:w-1/3 flex-1">
              <MultiSelectWithAllOption
                selectedOptions={personal.filter((p) =>
                  formik.values.personalIds?.includes(p.id)
                )}
                setSelectedOptions={(selected) =>
                  formik.setFieldValue(
                    "personalIds",
                    selected.map((p) => p.id)
                  )
                }
                options={personal}
                title={t("accounting.views.staff")}
              />
            </div>
            <div className="w-full lg:col-span-full lg:w-1/3 flex-1">
              <MultiSelect
                selectedOptions={
                  branches.filter((p) =>
                    formik.values.branchIds?.includes(p.id)
                  ) ||
                  branches.find(
                    (option) => option.value === formik.values.branchIds
                  )
                }
                setSelectedOptions={(selected) =>
                  formik.setFieldValue(
                    "branchIds",
                    selected.map((p) => p.id)
                  )
                }
                options={branches}
                title={t("accounting.views.branch")}
              />
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
